import { css } from "@emotion/css";
import { Avatar, Button } from "@octopusdeploy/design-system-components";
import { PlusIcon } from "@octopusdeploy/design-system-icons";
import { colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { getProjectTemplateIconUrl, type ProjectTemplateSummary } from "@octopusdeploy/octopus-server-client";
import type { ReactNode } from "react";
import React, { useState } from "react";
import { StepCard } from "~/areas/projects/components/Process/ActionTemplateSelector/StepCard";
import type { DoBusyTask } from "~/components/DataBaseComponent";
import { GuidedSetupFrame } from "~/components/OnboardingDialog/GuidedSetupFrame";
import addProjectImageDark from "~/components/ProjectBasedActivation/assets/img-addproject-dark.svg";
import addProjectImageLight from "~/components/ProjectBasedActivation/assets/img-addproject-light.svg";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import ProjectTemplateCardList from "./ProjectTemplateCardList";
interface NewProjectPageProps {
    title: ReactNode;
    projectTemplates: ProjectTemplateSummary[];
    isOnboarding?: boolean;
    onTemplateSkipped: () => void;
    onTemplateSelected?: (templateSlug: string) => void;
    close: () => void;
    spaceHasEnvironments: boolean;
    doBusyTask: DoBusyTask;
}
interface CreateBlankProjectCardProps {
    onClick: () => void;
}
function ProjectTemplateSelectionPage(props: NewProjectPageProps) {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedTemplateSummary, setSelectedTemplateSummary] = useState<ProjectTemplateSummary | null>(null);
    const isProjectTemplatesFeatureToggled = useOctopusFeatureToggle("project-templates", false);
    const handlePreviewClick = async (template: ProjectTemplateSummary) => {
        setSelectedTemplateSummary(template);
        setIsDrawerOpen(true);
    };
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
        setSelectedTemplateSummary(null);
    };
    const handleUseTemplate = (template: ProjectTemplateSummary) => {
        setIsDrawerOpen(false);
        setSelectedTemplateSummary(null);
        props.onTemplateSelected?.(template.Slug);
    };
    const helpPanelContent = (<div>
            <strong>Project Templates</strong> are used for standardization of projects and their dependencies. Get your project up and deployed quickly with a Project Template.
        </div>);
    const CreateBlankProjectCard = (props: CreateBlankProjectCardProps) => {
        return (<div className={styles.createBlankProjectCard.base}>
                <Button importance="quiet" size="medium" label={"Create Blank Project"} icon={<PlusIcon size={20}/>} onClick={props.onClick}/>
            </div>);
    };
    return (<GuidedSetupFrame helpPanelContent={helpPanelContent} helpPanelImage={{ src: addProjectImageLight, altText: "Add New Project", darkThemeSrc: addProjectImageDark }} showThemeSelector={props.isOnboarding} onClose={props.close} isLeftPanelWide={true}>
            <>
                <div className={styles.title}>{props.title}</div>
                <div className={styles.subheading}>Add Project</div>
                <div className={styles.heading}>Template Selection</div>

                {props.projectTemplates.length > 0 && (<ProjectTemplateCardList>
                        {[
                <CreateBlankProjectCard key={"create-blank-project-card"} onClick={props.onTemplateSkipped}/>,
                ...props.projectTemplates.map((projectTemplate, idx) => {
                    return (<StepCard key={`project-template-${idx}-card`} heading={projectTemplate.Name} subHeading={projectTemplate.Author ? `By ${projectTemplate.Author}` : ""} logo={<Avatar type="template" size="large" src={getProjectTemplateIconUrl(projectTemplate)} alt={`Icon for the project template called ${projectTemplate.Name}`}/>} primaryAction={{
                            label: "Use Template",
                            onClick: () => handleUseTemplate(projectTemplate),
                        }} secondaryAction={{
                            label: "Preview",
                            onClick: () => {
                                handlePreviewClick(projectTemplate);
                            },
                        }}/>);
                }),
            ]}
                    </ProjectTemplateCardList>)}
            </>
            {/* TODO: we need a non-blocking drawer component here */}
            {/* {isProjectTemplatesFeatureToggled && isDrawerOpen && selectedTemplateSummary && (
            <Drawer
                isOpen={isDrawerOpen}
                onClose={handleDrawerClose}
                title=""
                subtitle=""
                variant="customActions"
                layout="basic"
                width="narrow"
            >
                <ProjectTemplatePanel
                    projectTemplate={selectedTemplateSummary}
                    onClick={handleUseTemplate}
                />
            </Drawer>
        )} */}
        </GuidedSetupFrame>);
}
export default ProjectTemplateSelectionPage;
const styles = {
    title: css({
        marginTop: space[40],
        marginBottom: space[16],
    }),
    subheading: css({
        marginTop: space[32],
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
    }),
    heading: css({
        marginBottom: space[32],
        font: text.heading.large,
    }),
    createBlankProjectCard: {
        base: css({
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: colorScales.blue[100],
        }),
    },
};
